import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation resources
import configEn from './locales/en/config.json';
import configJa from './locales/ja/config.json';
import configFr from './locales/fr/config.json';

import commonEn from './locales/en/common.json';
import commonJa from './locales/ja/common.json';
import commonFr from './locales/fr/common.json';

import utilsEn from './locales/en/utils.json';
import utilsJa from './locales/ja/utils.json';
import utilsFr from './locales/fr/utils.json';

import viewsEn from './locales/en/views.json';
import viewsJa from './locales/ja/views.json';
import viewsFr from './locales/fr/views.json';

import genericsEn from './locales/en/generics.json';
import genericsJa from './locales/ja/generics.json';
import genericsFr from './locales/fr/generics.json';

const SUPPORTED_LANGUAGES = ['en', 'ja', 'fr'];

const normalizeLocale = (locale) => {
  if (!locale) {
    return null;
  }
  const languageCode = locale.split(/[-_]/)[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(languageCode) ? languageCode : null;
};

/**
 * Gets the user's preferred locale from localStorage or browser settings
 * @returns {string} The locale string (e.g., 'en', 'ja', 'fr')
 */
const getUserLocale = () => {
  // First priority: locale from API/user profile
  // Second priority: browser language
  // Third priority: legacy userLanguage storage
  // Default: English
  const apiLanguage = localStorage.getItem('apiLanguage');
  const userLanguage = localStorage.getItem('userLanguage');
  const browserLanguage = navigator.language;

  const normalizedApiLanguage = normalizeLocale(apiLanguage);
  const normalizedUserLanguage = normalizeLocale(userLanguage);
  const normalizedBrowserLanguage = normalizeLocale(browserLanguage);

  return normalizedApiLanguage || normalizedBrowserLanguage || normalizedUserLanguage || 'en';
};

// Define resources for all locales
const resources = {
  en: {
    config: configEn,
    common: commonEn,
    utils: utilsEn,
    views: viewsEn,
    generics: genericsEn
  },
  ja: {
    config: configJa,
    common: commonJa,
    utils: utilsJa,
    views: viewsJa,
    generics: genericsJa
  },
  fr: {
    config: configFr,
    common: commonFr,
    utils: utilsFr,
    views: viewsFr,
    generics: genericsFr
  }
};

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getUserLocale(),
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    // Namespace configuration
    defaultNS: 'views',
    ns: ['config', 'common', 'utils', 'views', 'generics'],
    
    interpolation: {
      escapeValue: false // React already escapes values
    },
    
    // React-specific options
    react: {
      useSuspense: false
    }
  });

/**
 * Reinitialize i18n with a new locale
 * This function can be called when the user locale is updated from API
 * @param {string} newLocale - The new locale to set (e.g., 'ja_JP', 'en-US')
 */
window.reinitializeI18n = (newLocale) => {
  if (newLocale) {
    // Persist API locale separately to avoid stale overrides of browser-first logic.
    localStorage.setItem('apiLanguage', newLocale);

    // Keep compatibility with older key usage while preventing stale priority issues.
    if (localStorage.getItem('userLanguage')) {
      localStorage.removeItem('userLanguage');
    }

    const preferredLocale = getUserLocale();

    if (preferredLocale !== i18n.language) {
      i18n.changeLanguage(preferredLocale);
    }
  }
};

export default i18n;